/**
 * ExportAuditLogsPage - Export audit logs and other data
 */
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import AdminSidebar from "../components/layout/AdminSidebar";

import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";

import {
  Download,
  FileText,
  FileSpreadsheet,
  ArrowLeft,
  Loader2,
  Calendar,
  Filter,
  BarChart3,
  Users,
  AlertTriangle,
  MessageSquare,
  RefreshCw
} from "lucide-react";

const ExportAuditLogsPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [stats, setStats] = useState(null);
  const [actionTypes, setActionTypes] = useState([]);
  const [entityTypes, setEntityTypes] = useState([]);
  
  // Filter state
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedAction, setSelectedAction] = useState("__all__");
  const [selectedEntity, setSelectedEntity] = useState("__all__");
  const [exportFormat, setExportFormat] = useState("csv");

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsRes, typesRes] = await Promise.all([
        axios.get(`${API}/export/audit-logs/stats?days=30`, { headers }),
        axios.get(`${API}/export/audit-logs/action-types`, { headers })
      ]);
      setStats(statsRes.data);
      setActionTypes(typesRes.data.action_types || []);
      setEntityTypes(typesRes.data.entity_types || []);
    } catch (error) {
      toast.error("Failed to load statistics");
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (exportType) => {
    setExporting(true);
    
    try {
      let url = `${API}/export/${exportType}?format=${exportFormat}`;
      
      if (exportType === "audit-logs") {
        if (startDate) url += `&start_date=${startDate}`;
        if (endDate) url += `&end_date=${endDate}`;
        if (selectedAction !== "__all__") url += `&action_type=${selectedAction}`;
        if (selectedEntity !== "__all__") url += `&entity_type=${selectedEntity}`;
      } else if (startDate || endDate) {
        if (startDate) url += `&start_date=${startDate}`;
        if (endDate) url += `&end_date=${endDate}`;
      }
      
      const response = await axios.get(url, {
        headers,
        responseType: 'blob'
      });
      
      // Get filename from content-disposition header
      const contentDisposition = response.headers['content-disposition'];
      let filename = `${exportType}_export.${exportFormat}`;
      if (contentDisposition) {
        const matches = contentDisposition.match(/filename=(.+)/);
        if (matches) filename = matches[1];
      }
      
      // Create download link
      const blob = new Blob([response.data], { 
        type: exportFormat === "csv" ? "text/csv" : "application/json" 
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success(`${exportType.replace('-', ' ')} exported successfully!`);
      
    } catch (error) {
      const message = error.response?.status === 404 
        ? "No data found matching your criteria"
        : "Export failed";
      toast.error(message);
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <AdminSidebar>
        <div className="min-h-screen bg-background flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </AdminSidebar>
    );
  }

  return (
    <AdminSidebar>
      <div className="min-h-screen bg-background" data-testid="export-audit-logs-page">
        {/* Header */}
        <header className="border-b bg-card sticky top-0 z-10">
          <div className="px-4 md:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Button variant="ghost" size="icon" onClick={() => navigate("/admin")}>
                  <ArrowLeft className="w-5 h-5" />
                </Button>
                <div className="flex items-center gap-2">
                  <Download className="w-6 h-6 text-primary" />
                  <div>
                    <h1 className="font-serif text-xl font-semibold">Export Data</h1>
                    <p className="text-sm text-muted-foreground">Export audit logs and reports</p>
                  </div>
                </div>
              </div>
              <Button variant="outline" onClick={loadData}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh Stats
              </Button>
            </div>
          </div>
        </header>

        <main className="px-4 md:px-8 py-6">
          {/* Stats Overview */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-primary/10">
                      <BarChart3 className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{stats.total_events?.toLocaleString() || 0}</p>
                      <p className="text-sm text-muted-foreground">Events (30d)</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-secondary/10">
                      <FileText className="w-5 h-5 text-secondary" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{Object.keys(stats.by_action || {}).length}</p>
                      <p className="text-sm text-muted-foreground">Action Types</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-accent/10">
                      <Users className="w-5 h-5 text-accent" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{Object.keys(stats.by_user || {}).length}</p>
                      <p className="text-sm text-muted-foreground">Active Users</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-green-500/10">
                      <Calendar className="w-5 h-5 text-green-500" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{stats.period_days || 30}</p>
                      <p className="text-sm text-muted-foreground">Days Analyzed</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          <Tabs defaultValue="audit-logs">
            <TabsList>
              <TabsTrigger value="audit-logs">
                <FileText className="w-4 h-4 mr-2" />
                Audit Logs
              </TabsTrigger>
              <TabsTrigger value="violations">
                <AlertTriangle className="w-4 h-4 mr-2" />
                Violations
              </TabsTrigger>
              <TabsTrigger value="owners">
                <Users className="w-4 h-4 mr-2" />
                Owners
              </TabsTrigger>
              <TabsTrigger value="ai-interactions">
                <MessageSquare className="w-4 h-4 mr-2" />
                AI Interactions
              </TabsTrigger>
            </TabsList>

            {/* Audit Logs Export */}
            <TabsContent value="audit-logs" className="mt-6">
              <Card>
                <CardHeader>
                  <CardTitle>Export Audit Logs</CardTitle>
                  <CardDescription>
                    Download system audit logs with optional filters
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Filters */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                      <Label>Start Date</Label>
                      <Input
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                      />
                    </div>
                    <div>
                      <Label>End Date</Label>
                      <Input
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                      />
                    </div>
                    <div>
                      <Label>Action Type</Label>
                      <Select value={selectedAction} onValueChange={setSelectedAction}>
                        <SelectTrigger>
                          <SelectValue placeholder="All actions" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__all__">All Actions</SelectItem>
                          {actionTypes.map(type => (
                            <SelectItem key={type} value={type}>
                              {type.replace(/_/g, ' ')}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Entity Type</Label>
                      <Select value={selectedEntity} onValueChange={setSelectedEntity}>
                        <SelectTrigger>
                          <SelectValue placeholder="All entities" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__all__">All Entities</SelectItem>
                          {entityTypes.map(type => (
                            <SelectItem key={type} value={type}>
                              {type.replace(/_/g, ' ')}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Format Selection */}
                  <div>
                    <Label>Export Format</Label>
                    <div className="flex gap-2 mt-2">
                      <Button
                        variant={exportFormat === "csv" ? "default" : "outline"}
                        onClick={() => setExportFormat("csv")}
                      >
                        <FileSpreadsheet className="w-4 h-4 mr-2" />
                        CSV
                      </Button>
                      <Button
                        variant={exportFormat === "json" ? "default" : "outline"}
                        onClick={() => setExportFormat("json")}
                      >
                        <FileText className="w-4 h-4 mr-2" />
                        JSON
                      </Button>
                    </div>
                  </div>

                  {/* Export Button */}
                  <Button
                    onClick={() => handleExport("audit-logs")}
                    disabled={exporting}
                    className="w-full md:w-auto"
                  >
                    {exporting ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4 mr-2" />
                    )}
                    Export Audit Logs
                  </Button>

                  {/* Top Actions Preview */}
                  {stats?.by_action && Object.keys(stats.by_action).length > 0 && (
                    <div className="mt-6 pt-4 border-t">
                      <h4 className="font-medium mb-3">Top Actions (Last 30 Days)</h4>
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(stats.by_action)
                          .sort((a, b) => b[1] - a[1])
                          .slice(0, 10)
                          .map(([action, count]) => (
                            <Badge key={action} variant="secondary">
                              {action.replace(/_/g, ' ')}: {count}
                            </Badge>
                          ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Violations Export */}
            <TabsContent value="violations" className="mt-6">
              <Card>
                <CardHeader>
                  <CardTitle>Export Violations</CardTitle>
                  <CardDescription>
                    Download violation records
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label>Start Date</Label>
                      <Input
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                      />
                    </div>
                    <div>
                      <Label>End Date</Label>
                      <Input
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                      />
                    </div>
                    <div>
                      <Label>Format</Label>
                      <Select value={exportFormat} onValueChange={setExportFormat}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="csv">CSV</SelectItem>
                          <SelectItem value="json">JSON</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <Button onClick={() => handleExport("violations")} disabled={exporting}>
                    {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                    Export Violations
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Owners Export */}
            <TabsContent value="owners" className="mt-6">
              <Card>
                <CardHeader>
                  <CardTitle>Export Owner Directory</CardTitle>
                  <CardDescription>
                    Download owner contact information and status
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Format</Label>
                      <Select value={exportFormat} onValueChange={setExportFormat}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="csv">CSV</SelectItem>
                          <SelectItem value="json">JSON</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <Button onClick={() => handleExport("owners")} disabled={exporting}>
                    {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                    Export Owners
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>

            {/* AI Interactions Export */}
            <TabsContent value="ai-interactions" className="mt-6">
              <Card>
                <CardHeader>
                  <CardTitle>Export AI Interactions</CardTitle>
                  <CardDescription>
                    Download AI assistant interaction logs
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label>Start Date</Label>
                      <Input
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                      />
                    </div>
                    <div>
                      <Label>End Date</Label>
                      <Input
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                      />
                    </div>
                    <div>
                      <Label>Format</Label>
                      <Select value={exportFormat} onValueChange={setExportFormat}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="csv">CSV</SelectItem>
                          <SelectItem value="json">JSON</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <Button onClick={() => handleExport("ai-interactions")} disabled={exporting}>
                    {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                    Export AI Interactions
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </main>
      </div>
    </AdminSidebar>
  );
};

export default ExportAuditLogsPage;
